// ── Google Analytics 4 (GA4) Utility ──────────────────────────────────────
// Measurement ID: G-MSYFZHY6WK
// Usage:
//   import { gaEvent, gaPageView } from '../utils/analytics';
//   gaEvent('tab_switch', { tab_name: 'Sea-Export' });
// ──────────────────────────────────────────────────────────────────────────

const GA_ID = 'G-MSYFZHY6WK';

/** Returns true when gtag is loaded */
const isReady = () => typeof window !== 'undefined' && typeof window.gtag === 'function';

// ── Page View ──────────────────────────────────────────────────────────────
/**
 * Track a virtual page view (call on every route change).
 * @param {string} path   e.g. '/user'
 * @param {string} title  e.g. 'Customer Dashboard'
 */
export const gaPageView = (path, title) => {
  if (!isReady()) return;
  window.gtag('config', GA_ID, {
    page_path: path,
    page_title: title || document.title,
  });
};

// ── Generic Event ──────────────────────────────────────────────────────────
/**
 * Send a custom GA4 event.
 * @param {string} eventName   snake_case event name
 * @param {object} params      extra dimensions / metrics
 */
export const gaEvent = (eventName, params = {}) => {
  if (!isReady()) return;
  window.gtag('event', eventName, params);
};

// ── Pre-built Customer Dashboard Events ───────────────────────────────────

/** Fired once when the customer dashboard mounts */
export const gaUserDashboardLoad = (userId, role) => {
  gaEvent('dashboard_load', {
    user_id_hint: userId,   // non-PII identifier
    user_role: role,
    dashboard: 'customer',
  });
};

/** Tab switched: Air-Export / Sea-Export */
export const gaTabSwitch = (tabName) => {
  gaEvent('tab_switch', { tab_name: tabName, dashboard: 'customer' });
};

/** AWB search performed */
export const gaAWBSearch = (query, activeTab) => {
  gaEvent('awb_search', {
    search_term: query,
    active_tab: activeTab,
    dashboard: 'customer',
  });
};

/** Date-range filter applied */
export const gaDateFilter = (startDate, endDate, activeTab) => {
  gaEvent('date_filter_apply', {
    start_date: startDate,
    end_date: endDate,
    active_tab: activeTab,
    dashboard: 'customer',
  });
};

/** Country filter applied */
export const gaCountryFilter = (country, activeTab) => {
  gaEvent('country_filter', { country, active_tab: activeTab, dashboard: 'customer' });
};

/** Customer name filter applied (for admin/accountant viewing multiple) */
export const gaCustomerFilter = (customerCount, activeTab) => {
  gaEvent('customer_filter', { selected_count: customerCount, active_tab: activeTab, dashboard: 'customer' });
};

/** Shipment row clicked → detail view */
export const gaShipmentView = (awbNumber, shipmentType) => {
  gaEvent('shipment_view', {
    awb_number: awbNumber,
    shipment_type: shipmentType,
    dashboard: 'customer',
  });
};

// ── PDF Download Events (one per document type) ───────────────────────────

/** AWB Copy opened */
export const gaDownloadAWBCopy = (awbNumber, activeTab) => {
  gaEvent('pdf_awb_copy_open', {
    awb_number: awbNumber,
    document_type: 'AWB Copy',
    active_tab: activeTab,
    dashboard: 'customer',
  });
};

/** LEO document(s) opened */
export const gaDownloadLEO = (awbNumber, leoCount, activeTab) => {
  gaEvent('pdf_leo_open', {
    awb_number: awbNumber,
    document_type: 'LEO',
    leo_count: leoCount ?? 1,
    active_tab: activeTab,
    dashboard: 'customer',
  });
};

/** INV / Packing List opened */
export const gaDownloadINV = (awbNumber, activeTab) => {
  gaEvent('pdf_inv_pl_open', {
    awb_number: awbNumber,
    document_type: 'INV/PL',
    active_tab: activeTab,
    dashboard: 'customer',
  });
};

/** HMR Invoice opened */
export const gaDownloadInvoice = (awbNumber, activeTab) => {
  gaEvent('pdf_invoice_open', {
    awb_number: awbNumber,
    document_type: 'INVOICE',
    active_tab: activeTab,
    dashboard: 'customer',
  });
};

/** "Air Export Insights" button clicked */
export const gaInsightsOpen = () => {
  gaEvent('insights_open', { dashboard: 'customer' });
};

/** Shipment count loaded (useful metric) */
export const gaShipmentsLoaded = (count, tabName) => {
  gaEvent('shipments_loaded', {
    shipment_count: count,
    tab_name: tabName,
    dashboard: 'customer',
  });
};

// ── Air Export Insights Events ─────────────────────────────────────────────

/** Insights page loaded */
export const gaInsightsLoad = (shipmentCount) => {
  gaEvent('insights_page_load', {
    shipment_count: shipmentCount,
    dashboard: 'insights',
  });
};

/** Insights section tab switched (Overview / Global Presence / Top Clients / Incentive) */
export const gaInsightsTabSwitch = (tabId, tabLabel) => {
  gaEvent('insights_tab_switch', {
    tab_id: tabId,
    tab_label: tabLabel,
    dashboard: 'insights',
  });
};

/** Year filter chip clicked */
export const gaInsightsYearFilter = (year, action) => {
  gaEvent('insights_year_filter', {
    selected_year: year,
    action, // 'apply' | 'clear'
    dashboard: 'insights',
  });
};

/** Month bar clicked on chart → drills down */
export const gaInsightsMonthDrilldown = (monthKey, activeTab) => {
  gaEvent('insights_month_drilldown', {
    month_key: monthKey,
    active_tab: activeTab,
    dashboard: 'insights',
  });
};

/** Country clicked on chart / map → drills down */
export const gaInsightsCountryDrilldown = (country, activeTab) => {
  gaEvent('insights_country_drilldown', {
    country,
    active_tab: activeTab,
    dashboard: 'insights',
  });
};

/** Reset selection (month + country cleared) */
export const gaInsightsResetFilter = () => {
  gaEvent('insights_filter_reset', { dashboard: 'insights' });
};

// ── Auth Events ────────────────────────────────────────────────────────────

/** User login success (role-aware) */
export const gaLoginSuccess = (role) => {
  gaEvent('login_success', { user_role: role });
};

/** User logout */
export const gaLogout = (role) => {
  gaEvent('logout', { user_role: role });
};
